const DefaultPalette = (mode, skin, themeColor) => {
  // ** Vars
  const whiteColor = '#FFF'
  const lightColor = '58, 53, 65'
  const darkColor = '231, 227, 252'
  const mainColor = mode === 'light' ? lightColor : darkColor

  const lightColorRgb = `rgb(${lightColor})`
  const darkColorRgb = `rgb(${darkColor})`
  const mainColorRgb = mode === 'light' ? lightColorRgb : darkColorRgb

  const primaryGradient = () => {
    if (themeColor === 'primary') {
      return '#51ADCF'
    } else if (themeColor === 'secondary') {
      return '#9C9FA4'
    } else if (themeColor === 'success') {
      return '#93DD5C'
    } else if (themeColor === 'error') {
      return '#FF8C90'
    } else if (themeColor === 'warning') {
      return '#FFCF5C'
    } else {
      return '#6ACDFF'
    }
  }

  const defaultBgColor = () => {
    if (skin === 'bordered' && mode === 'light') {
      return whiteColor
    } else if (skin === 'bordered' && mode === 'dark') {
      return '#312D4B'
    } else if (mode === 'light') {
      return '#F4F5FA'
    } else return '#28243D'
  }

  return {
    customColors: {
      dark: darkColorRgb,
      main: mainColorRgb,
      light: lightColorRgb,
      darkChannel: darkColor,
      mainChannel: mainColor,
      lightChannel: lightColor,
      primaryGradient: primaryGradient(),
      bodyBg: mode === 'light' ? '#F4F5FA' : '#28243D',
      trackBg: mode === 'light' ? '#F0F2F8' : '#474360',
      avatarBg: mode === 'light' ? '#F0EFF0' : '#3F3B59',
      darkBg: skin === 'bordered' ? '#312D4B' : '#28243D',
      lightBg: skin === 'bordered' ? whiteColor : '#F4F5FA',
      tableHeaderBg: mode === 'light' ? '#F9FAFC' : '#3D3759',
      skyPalettePrimary: '#0278AE',
      skyPaletteSecondary: '#1B6B93',
      skyPaletteTertiary: '#1B8A6B',

      /*
        Variante oscura del terciario, para cuando ese verde tiene que llevar
        texto encima. #1B8A6B con el titulo claro daba 3,75:1; #136048 con blanco
        da 7,51:1. El claro se conserva porque es el que pinta los bordes
        decorativos de las tarjetas, donde no hay texto y el contraste no aplica.
      */
      skyPaletteTertiaryDark: '#136048',
      skyPaletteQuaternary: '#5A8A9E',
      skyPaletteTitle: '#E8F1F5',

      /*
        Versiones legibles de success y warning para cuando se usan como color de
        TEXTO sobre fondo claro. Los `main` (#4dddb2 y #FFB400) estan pensados
        para pintar rellenos: como texto sobre blanco dan 1,67:1 y 1,74:1.
        Estos dan 6,03 y 6,72. Para fondos y bordes se sigue usando el `main`.
      */
      /*
        Colores semanticos del juego (rareza del equipo y nivel de mejora). Los
        tonos pensados para fondo blanco se vuelven ilegibles sobre el papel
        oscuro: el verde de mejora daba 2,00:1 y el naranja 2,33:1. Cada uno
        tiene su version por modo, conservando el matiz que identifica la rareza.
      */
      rarezaAzul: mode === 'light' ? '#216de7' : '#6FA8FF',
      rarezaMorado: mode === 'light' ? '#7028dd' : '#B18AFF',
      rarezaNaranja: mode === 'light' ? '#96591A' : '#E0A458',
      mejora: mode === 'light' ? '#0c6d0d' : '#5FD35F',
      // rojo de "falta" legible en los dos modos: #C81414 da 2,21:1 sobre el papel oscuro
      faltaText: mode === 'light' ? '#C81414' : '#FF8A80',

      successText: mode === 'light' ? '#157056' : '#95e0c9',
      warningText: mode === 'light' ? '#7A5500' : '#FFCA64'
    },
    mode: mode,
    common: {
      black: '#000',
      white: whiteColor
    },
    /*
      El azul de marca cambia segun el modo.

      #0278AE esta pensado para fondo blanco, donde da 4,9:1. Sobre el papel del
      modo oscuro (#312D4B) cae a 2,67:1, y como es el color de todos los
      enlaces y de los titulos de seccion, en oscuro fallaban 78 elementos en
      solo cinco paginas: cada "CHECK TOOL", los encabezados del bloque de
      contenido y el pie entero. No se veia en las auditorias porque Lighthouse
      mide en claro y el modo oscuro se activa a mano.

      #38B6F0 da 5,67:1 sobre el papel oscuro y 6,47:1 sobre el fondo. Es el
      mismo azul, aclarado lo justo.

      contrastText acompana: en oscuro el primary es claro, asi que lo que va
      encima —botones contenidos, item activo del menu— tiene que ser tinta
      oscura (6,77:1) y no blanco (2,30:1).
    */
    primary:
      mode === 'light'
        ? { light: '#0287C3', main: '#0278AE', dark: '#02618C', contrastText: whiteColor }
        : { light: '#5BC8F7', main: '#38B6F0', dark: '#2FA8E8', contrastText: '#06263A' },
    secondary: {
      light: '#9C9FA4',
      main: '#8A8D93',
      dark: '#777B82',
      contrastText: whiteColor
    },
    /*
      En oscuro el rojo tambien se aclara: #cb4a4e sobre el papel #312D4B da
      2,88:1, y MUI lo usa como color de texto en los botones outlined y en los
      Alert. #F28B82 da 5,47:1, y lo que va encima pasa a tinta oscura.
    */
    error:
      mode === 'light'
        ? { light: '#d1898b', main: '#cb4a4e', dark: '#a03e41', contrastText: whiteColor }
        : { light: '#FF9B94', main: '#F28B82', dark: '#E57373', contrastText: '#2B0A0A' },
    /*
      contrastText y .dark de warning, info y success cambiados por contraste.

      Los tres son colores claros y llevaban texto blanco encima, que es
      justamente donde peor funciona: medido sobre el color plano, blanco daba
      1,78:1 en warning, 2,40:1 en info y 1,71:1 en success. WCAG AA pide 4,5:1.
      El chip "Beginner" de /guides/ y los botones de estado eran ilegibles.

      Los tonos .dark quedan como estaban: MUI los usa como fondo de hover de los
      botones, y con el contrastText nuevo siguen dando 5,89:1 (success), 7,87:1
      (warning) y 5,01:1 (info). El texto ilegible de los <Alert> no venia de
      aca sino de darken(main, 0.12) en overrides/alerts.js, que es donde se
      corrige.

      error no se toca: su blanco sobre #cb4a4e ya daba 4,54:1.
    */
    warning: {
      light: '#FFCA64',
      main: '#FFB400',
      dark: '#E09E00',
      contrastText: '#1F1300'
    },
    info: {
      light: '#32BAFF',
      main: '#16B1FF',
      dark: '#139CE0',
      contrastText: '#04283A'
    },
    success: {
      light: '#95e0c9',
      main: '#4dddb2',
      dark: '#36b891',
      contrastText: '#0B2E24'
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#F5F5F5',
      A200: '#EEEEEE',
      A400: '#BDBDBD',
      A700: '#616161'
    },
    text: {
      primary: `rgba(${mainColor}, 0.87)`,

      /*
        0,6 -> 0,72. Con 0,6 el texto secundario resolvia a #89868d sobre
        blanco: 3,58:1 de contraste, por debajo del 4,5:1 que exige WCAG AA
        para texto normal. Lighthouse lo marcaba dos veces en la home, una de
        ellas justo en el parrafo que es el elemento LCP.

        0,72 da 5,0:1 sobre blanco. En modo oscuro sube el alfa de un color
        claro sobre fondo oscuro, asi que tambien mejora ahi.
      */
      secondary: `rgba(${mainColor}, 0.72)`,
      disabled: `rgba(${mainColor}, 0.38)`
    },
    divider: `rgba(${mainColor}, 0.12)`,
    background: {
      paper: mode === 'light' ? whiteColor : '#312D4B',
      default: defaultBgColor()
    },
    action: {
      active: `rgba(${mainColor}, 0.54)`,
      hover: `rgba(${mainColor}, 0.04)`,
      selected: `rgba(${mainColor}, 0.08)`,
      disabled: `rgba(${mainColor}, 0.26)`,
      disabledBackground: `rgba(${mainColor}, 0.12)`,
      focus: `rgba(${mainColor}, 0.12)`
    }
  }
}

export default DefaultPalette
